import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import type { Insights } from '../hooks/useInsights'

interface TopQueriesTableProps {
  queries: Insights['topQueries'] | undefined
  isLoading: boolean
}

export function TopQueriesTable({ queries, isLoading }: TopQueriesTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Top Searches
        </CardTitle>
        <CardDescription>What customers are looking for</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !queries || queries.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
            No search data yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="pb-2 font-medium">Query</th>
                  <th className="pb-2 text-right font-medium">Searches</th>
                  <th className="pb-2 text-right font-medium">CTR</th>
                  <th className="pb-2 text-right font-medium">Avg results</th>
                  <th className="pb-2 text-right font-medium">Trend</th>
                </tr>
              </thead>
              <tbody>
                {queries.slice(0, 15).map((q) => (
                  <tr key={q.query} className="border-b last:border-0">
                    <td className="py-2 font-medium">{q.query}</td>
                    <td className="py-2 text-right tabular-nums">{q.searches.toLocaleString()}</td>
                    <td className="py-2 text-right tabular-nums">
                      <span
                        className={
                          q.ctr >= 20
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : q.ctr >= 10
                              ? ''
                              : 'text-muted-foreground'
                        }
                      >
                        {q.ctr}%
                      </span>
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {q.avgResults > 0 ? q.avgResults.toFixed(0) : '–'}
                    </td>
                    <td className="py-2 text-right">
                      <TrendCell trend={q.trend} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TrendCell({ trend }: { trend: number | null }) {
  if (trend === null) {
    return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">new</span>
  }
  if (trend === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
      </span>
    )
  }
  const Icon = trend > 0 ? TrendingUp : TrendingDown
  const colour = trend > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium tabular-nums ${colour}`}>
      <Icon className="h-3 w-3" />
      {trend > 0 ? '+' : ''}
      {trend}%
    </span>
  )
}
